import React, { createContext, useContext, useRef, useState } from 'react';
import { useForm } from 'formydable';
import createDimension, { useDimension } from 'dymension';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Alert from 'react-bootstrap/Alert';
import ServicesConfig from '../../ServicesConfig';
import FormManager from '../../FormManager';
import Loader from '../../Loader';
import Icon from '../../Icon';
import GraphIndicator from '../../GraphIndicator';
import FullScreen from '../../FullScreen';
import HelpText from '../../HelpText';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import '../index.scss';
const ModalFormContext = /*#__PURE__*/createContext(null);
export const useModalForm = () => useContext(ModalFormContext);

const FormModal = ({
  id,
  title,
  titleIcon,
  body,
  size,
  className,
  headerClassName,
  footerClassName,
  bodyClassName,
  centered,
  allowFullscreen,
  activateGraphIndicator,
  backdrop,
  submit,
  cancel,
  confirm,
  block,
  noValidate,
  genericHelpText,
  customFormManager,
  viewOnly,
  prependFooter
}) => {
  const fullscreenRef = useRef(null);
  const form = useForm();
  const [{
    confirmation,
    loading,
    error,
    validationMessages
  }, setStates] = useState({
    confirmation: false,
    loading: false,
    error: null,
    validationMessages: null
  });
  const {
    show,
    resolve,
    close
  } = useDimension();

  const handleCancel = () => {
    if (block && form.dirty) {
      setStates(prev => ({ ...prev,
        confirmation: true
      }));
    } else {
      resolve({
        isSubmit: false
      });
      close();
      fullscreenRef.current?.exitFullscreen();
    }
  };

  const handleSubmit = event => {
    setStates(prev => ({ ...prev,
      loading: true
    }));
    resolve(Object.assign(event, {
      closeModal: () => {
        close();
        fullscreenRef.current?.exitFullscreen();
      },
      exitFullscreen: () => fullscreenRef.current?.exitFullscreen(),
      removeLoading: () => setStates(prev => ({ ...prev,
        loading: false
      })),
      setError: error => setStates(prev => ({ ...prev,
        error
      })),
      setValidationMessages: validationMessages => setStates(prev => ({ ...prev,
        validationMessages
      })),
      isSubmit: true
    }));
  };

  const handleConfirmCancel = () => {
    setStates(prev => ({ ...prev,
      confirmation: false
    }));
  };

  const handleConfirmSubmit = () => {
    resolve({
      isSubmit: false
    });
    close();
    fullscreenRef.current?.exitFullscreen();
  };

  return /*#__PURE__*/React.createElement(Modal, {
    show: show,
    size: size,
    className: className,
    centered: centered,
    backdrop: backdrop,
    onHide: handleCancel,
    autoFocus: false
  }, /*#__PURE__*/React.createElement(ServicesConfig, null, /*#__PURE__*/React.createElement(FullScreen, {
    ref: fullscreenRef
  }, /*#__PURE__*/React.createElement(Modal.Header, {
    className: headerClassName,
    closeButton: true
  }, /*#__PURE__*/React.createElement(Modal.Title, null, titleIcon ? /*#__PURE__*/React.createElement("div", {
    className: "d-flex align-items-center"
  }, /*#__PURE__*/React.createElement("i", {
    className: titleIcon,
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("div", {
    className: "ml-3"
  }, title)) : title), /*#__PURE__*/React.createElement(HelpText, {
    as: "button",
    groupName: "Modal Form",
    className: "close other-action ml-auto",
    removeModalTrapFocus: true,
    customURL: genericHelpText ? `/generic-modal?modal=${id}` : `${window.location.pathname}?modal=${id}`
  }), allowFullscreen && /*#__PURE__*/React.createElement(FullScreen.Trigger, {
    as: "button",
    type: "button",
    className: "close other-action",
    render: isFullscreen => /*#__PURE__*/React.createElement(Icon, {
      title: isFullscreen ? 'exit fullscreen' : 'enter fullscreen',
      name: isFullscreen ? 'compress' : 'expand'
    })
  })), /*#__PURE__*/React.createElement(Modal.Body, {
    className: bodyClassName
  }, /*#__PURE__*/React.createElement(GraphIndicator, {
    activate: activateGraphIndicator
  }, !!error && /*#__PURE__*/React.createElement(Alert, {
    className: "modal-error-message",
    variant: "danger",
    onClose: () => setStates(prev => ({ ...prev,
      error: null
    })),
    dismissible: true
  }, error), customFormManager ? /*#__PURE__*/React.createElement(ModalFormContext.Provider, {
    value: {
      formMethods: form,
      onSubmit: handleSubmit,
      id,
      noValidate
    }
  }, body) : /*#__PURE__*/React.createElement(FormManager.Core, {
    value: form,
    onSubmit: handleSubmit,
    id: id,
    noValidate: noValidate
  }, body)), !!validationMessages && /*#__PURE__*/React.createElement(Row, {
    className: "modal-validation-messages"
  }, /*#__PURE__*/React.createElement(Col, {
    sm: ['lg', 'xl'].includes(size || 'md') ? {
      span: 6,
      offset: 6
    } : 12
  }, /*#__PURE__*/React.createElement(Alert, {
    variant: "danger",
    className: "mb-0",
    onClose: () => setStates(prev => ({ ...prev,
      validationMessages: null
    })),
    dismissible: true
  }, /*#__PURE__*/React.createElement("ul", {
    className: "m-0 pl-3"
  }, validationMessages.map((each, index) => /*#__PURE__*/React.createElement("li", {
    key: index
  }, each))))))), /*#__PURE__*/React.createElement(Modal.Footer, {
    className: footerClassName
  }, confirmation ? /*#__PURE__*/React.createElement(Alert, {
    variant: "danger"
  }, /*#__PURE__*/React.createElement("h4", null, confirm.title || 'You are leaving'), /*#__PURE__*/React.createElement("p", {
    className: "text-justify"
  }, confirm.body || 'Are you sure, you want to leave? the data cannot be retrieved after this action.'), /*#__PURE__*/React.createElement("div", {
    className: "d-flex justify-content-end"
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "secondary",
    className: "mr-3",
    onClick: handleConfirmCancel
  }, confirm.cancel || 'Stay'), /*#__PURE__*/React.createElement(Button, {
    variant: "primary",
    onClick: handleConfirmSubmit
  }, confirm.submit || 'Leave Anyway'))) : /*#__PURE__*/React.createElement(React.Fragment, null, prependFooter && prependFooter(close), !viewOnly && /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    form: id,
    variant: "primary"
  }, submit), /*#__PURE__*/React.createElement(Button, {
    variant: "secondary",
    onClick: handleCancel
  }, cancel))), /*#__PURE__*/React.createElement(Loader, {
    active: loading
  }))));
};

FormModal.defaultProps = {
  id: "prompt-submit",
  backdrop: 'static',
  submit: 'Save',
  cancel: 'Cancel',
  confirm: {},
  block: false
};
//@ts-ignore
const ModalForm = createDimension(FormModal, {
  delay: 150,
  closeManually: true,
  manipulateWrapperNode: (wrapperNode, props) => {
    if (props.key) {
      const existNode = document.getElementById(props.key);

      if (existNode) {
        wrapperNode = existNode;
      } else {
        wrapperNode.id = props.key;
      }
    }

    return wrapperNode;
  }
});
ModalForm.Row = Row;
ModalForm.Col = Col;
export const ModalFormOverlay = createDimension(FormModal, {
  delay: 150,
  closeManually: true,
  manipulateWrapperNode: (wrapperNode, props) => {
    if (props.key) {
      const existNode = document.getElementById(props.key);

      if (existNode) {
        wrapperNode = existNode;
      } else {
        wrapperNode.id = props.key;
      }
    }

    return wrapperNode;
  }
});
export default ModalForm;